import { FC } from 'react';
import styled from 'styled-components';

import { InvoiceBuyerResource } from '@/types/resources';
import { paymentMethodLabels } from '@/utils/data/payments';
import { PaymentMethod } from '@/utils/enums';

import { sumAmounts } from '../invoiceTotals';

export interface InvoiceHtmlProps {
  currency: 'EUR' | 'USD' | 'GBP';
  amount: number;
  hours?: number;
  bonus?: number;
  bonusPurpose?: string;
  paymentPurpose: string;
  invoiceNumber?: string;
  freelancerName: string;
  freelancerCountry: string;
  freelancerVatNumber?: string;
  freelancerAddress?: string;
  freelancerPostCode?: string;
  freelancerActivityCode?: string;
  freelancerPersonalCode?: string;
  bankIban: string;
  bankName: string;
  bankSwift?: string;
  trueReceiver?: string;
  /** The bank account belongs to a company, so it is also the seller. */
  isCompanyReceiver?: boolean;
  paymentMethod?: PaymentMethod;
  walletEmail?: string;
  vat: number;
  date: string;
  amounts?: { amount: number; hours?: number; paymentPurpose: string }[];
  bonuses?: { amount: number; purpose?: string }[];
  buyer?: InvoiceBuyerResource | null;
}

const Wrap = styled.div`
  font-family: 'DejaVu Sans', Arial, sans-serif;
  font-size: 12px;
  line-height: 15px;
  color: #000;
  background: #fff;
  padding: 30px;
  width: 640px;
  max-height: 905px;
`;

const H1 = styled.h1`
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
  margin-top: 0;
  margin-bottom: 12px;
`;

const InvoiceHeader = styled.div`
  margin-bottom: 40px;
`;

const HeaderTable = styled.table`
  td:first-child {
    width: 120px;
  }
  td,
  th {
    padding: 2px 0;
  }
  border-collapse: collapse;
`;

const InfoHeading = styled.p`
  font-weight: 700;
  margin-bottom: 6px;
  margin-top: 0;
`;

const InfoTable = styled.table`
  width: 100%;
  margin-bottom: 48px;
  border-collapse: collapse;
  td:first-child {
    vertical-align: top;
    width: 60%;
  }
  td:last-child {
    vertical-align: top;
    width: 40%;
  }
`;

const MainTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 40px;
  th {
    font-weight: 400;
    border-bottom: solid 1px #000;
    padding: 6px 8px;
    text-align: left;
  }
  td {
    padding: 6px 8px;
    text-align: left;
  }
  /* Description column — keep long payment purposes from squeezing the
     numeric columns; wrap instead. Flush to the table's outer edges. */
  th:first-child,
  td:first-child {
    max-width: 220px;
    overflow-wrap: break-word;
    padding-left: 0;
  }
  th:last-child,
  td:last-child {
    text-align: right;
    padding-right: 0;
  }
`;

const SummaryTable = styled.table`
  margin-left: auto;
  margin-bottom: 36px;
  border-collapse: collapse;
  th,
  td {
    border-top: solid 1px #e9e9e9;
    padding: 6px 0;
  }
  th:first-child,
  td:first-child {
    text-align: left;
    padding-right: 100px;
  }
  th:last-child,
  td:last-child {
    text-align: right;
  }
`;

const BonusRow = styled.tr`
  td {
    border-top: solid 1px #e9e9e9;
  }
`;

const BottomBreak = styled.div`
  border-bottom: solid 1px #000;
  margin: 24px 0;
`;

const P = styled.p`
  margin: 0 0 4px 0;
`;

const CURRENCY_SIGNS: Record<string, string> = { EUR: '€', GBP: '£', USD: '$' };
const fmt = (n: number) => n.toFixed(2);

const InvoiceHtml: FC<InvoiceHtmlProps> = ({
  currency,
  amount,
  hours,
  bonus,
  bonusPurpose,
  paymentPurpose,
  invoiceNumber,
  freelancerName,
  freelancerCountry,
  freelancerVatNumber,
  freelancerAddress,
  freelancerPostCode,
  freelancerActivityCode,
  freelancerPersonalCode,
  bankIban,
  bankName,
  bankSwift,
  trueReceiver,
  isCompanyReceiver,
  paymentMethod = PaymentMethod.Bank,
  walletEmail,
  vat,
  date,
  amounts,
  bonuses,
  buyer,
}) => {
  const cs = CURRENCY_SIGNS[currency] ?? '$';
  const isLT = freelancerCountry.toUpperCase() === 'LT';
  // Freelancers invoicing under an activity code are identified by it; the
  // rest fall back to their personal code. Both print as a bare line.
  const sellerCode = freelancerActivityCode || freelancerPersonalCode;
  // The account holder is only named in the bank details, unless it is a
  // company invoicing in its own name.
  const accountHolder = trueReceiver || freelancerName;
  const sellerName = isCompanyReceiver ? accountHolder : freelancerName;
  // Same order as the PDF's buyer partial.
  const buyerLocality = [buyer?.city, buyer?.country, buyer?.post_code]
    .filter(Boolean)
    .join(', ');

  const amountsTotal = amounts?.length ? sumAmounts(amounts) : amount;
  const bonusTotal = bonuses?.length ? sumAmounts(bonuses) : bonus ?? 0;
  const subtotal = amountsTotal + bonusTotal;
  const total = subtotal + vat;

  // Hour-based lines carry their unit; anything else is a single item.
  const quantity = (h?: number) =>
    h ? `${h} ${isLT ? 'val' : h === 1 ? 'hour' : 'hours'}` : 1;

  const t = {
    heading: isLT
      ? freelancerVatNumber
        ? 'PVM sąskaita faktūra'
        : 'Sąskaita faktūra'
      : freelancerVatNumber
        ? 'VAT Invoice'
        : 'Invoice',
    invoiceNumber: isLT ? 'Sąskaitos numeris:' : 'Invoice number:',
    dateOfIssue: isLT ? 'Išrašymo data:' : 'Date of issue:',
    seller: isLT ? 'Pardavėjas' : 'Seller',
    bankDetails: isLT ? 'Banko duomenys' : 'Bank Details',
    billTo: isLT ? 'Pirkėjas' : 'Bill to',
    description: isLT ? 'Aprašymas' : 'Description',
    quantity: isLT ? 'Kiekis' : 'Quantity',
    unitPrice: isLT ? 'Vnt. kaina' : 'Unit Price',
    amount: isLT ? 'Suma' : 'Amount',
    bonus: isLT ? 'Priedas' : 'Bonus',
    subtotal: isLT ? 'Viso' : 'Subtotal',
    tax: isLT ? 'PVM (21%)' : 'Tax (0%)',
    amountDue: isLT ? 'Suma' : 'Amount Due',
  };

  return (
    <Wrap>
      <InvoiceHeader>
        <H1>{t.heading}</H1>
        <HeaderTable>
          <tbody>
            <tr>
              <td>{t.invoiceNumber}</td>
              <td>{invoiceNumber ?? '*Will be generated*'}</td>
            </tr>
            <tr>
              <td>{t.dateOfIssue}</td>
              <td>{date}</td>
            </tr>
          </tbody>
        </HeaderTable>
      </InvoiceHeader>

      <InfoTable>
        <tbody>
          <tr>
            <td>
              <InfoHeading>{t.seller}</InfoHeading>
              <P>{sellerName}</P>
              {(freelancerAddress || freelancerPostCode) && (
                <P>
                  {freelancerAddress}
                  {freelancerPostCode ? `, ${freelancerPostCode}` : ''}
                </P>
              )}
              {sellerCode && <P>{sellerCode}</P>}
              {freelancerVatNumber && <P>{freelancerVatNumber}</P>}
            </td>
            <td>
              <InfoHeading>{t.billTo}</InfoHeading>
              {buyer && (
                <>
                  <P>{buyer.name}</P>
                  {buyer.registration_code && <P>{buyer.registration_code}</P>}
                  {buyer.vat_code && <P>{buyer.vat_code}</P>}
                  {buyer.address && <P>{buyer.address}</P>}
                  {buyerLocality && <P>{buyerLocality}</P>}
                </>
              )}
            </td>
          </tr>
        </tbody>
      </InfoTable>

      <MainTable>
        <thead>
          <tr>
            <th>
              <strong>{t.description}</strong>
            </th>
            <th>
              <strong>{t.quantity}</strong>
            </th>
            <th>
              <strong>{t.unitPrice}</strong>
            </th>
            <th>
              <strong>{t.amount}</strong>
            </th>
          </tr>
        </thead>
        <tbody>
          {amounts?.length ? (
            amounts.map((li, i) => (
              <tr key={`amount-${i}`}>
                <td>{li.paymentPurpose}</td>
                <td>{quantity(li.hours)}</td>
                <td>
                  {cs} {fmt(li.hours ? li.amount / li.hours : li.amount)}
                </td>
                <td>
                  {cs} {fmt(li.amount)}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td>{paymentPurpose}</td>
              <td>{quantity(hours)}</td>
              <td>
                {cs} {fmt(hours ? amount / hours : amount)}
              </td>
              <td>
                {cs} {fmt(amount)}
              </td>
            </tr>
          )}
          {bonuses?.length
            ? bonuses.map((b, i) => (
                <BonusRow key={`bonus-${i}`}>
                  <td>{b.purpose ?? t.bonus}</td>
                  <td>1</td>
                  <td>
                    {cs} {fmt(b.amount)}
                  </td>
                  <td>
                    {cs} {fmt(b.amount)}
                  </td>
                </BonusRow>
              ))
            : !!bonus && (
                <BonusRow>
                  <td>{bonusPurpose ?? t.bonus}</td>
                  <td>1</td>
                  <td>
                    {cs} {fmt(bonus)}
                  </td>
                  <td>
                    {cs} {fmt(bonus)}
                  </td>
                </BonusRow>
              )}
        </tbody>
      </MainTable>

      <SummaryTable>
        <thead>
          <tr>
            <th>{t.subtotal}</th>
            <th>
              {cs} {fmt(subtotal)}
            </th>
          </tr>
        </thead>
        <tbody>
          {!!freelancerVatNumber && (
            <tr>
              <td>{t.tax}</td>
              <td>
                {cs} {fmt(vat)}
              </td>
            </tr>
          )}
          <tr>
            <td>
              <strong>{t.amountDue}</strong>
            </td>
            <td>
              <strong>
                {cs} {fmt(total)}
              </strong>
            </td>
          </tr>
        </tbody>
      </SummaryTable>

      <BottomBreak />
      <InfoTable>
        <tbody>
          <tr>
            <td>
              <InfoHeading>{t.bankDetails}</InfoHeading>
              <P>{accountHolder}</P>
              {paymentMethod !== PaymentMethod.Bank ? (
                <>
                  <P>{walletEmail}</P>
                  <P>{paymentMethodLabels[paymentMethod]}</P>
                </>
              ) : (
                <>
                  <P>{bankIban}</P>
                  <P>{bankName}</P>
                  {!isLT && bankSwift && <P>{bankSwift}</P>}
                </>
              )}
            </td>
          </tr>
        </tbody>
      </InfoTable>
    </Wrap>
  );
};

export default InvoiceHtml;
